import { useCallback } from 'react';

import type { EditorView } from '@atlaskit/editor-prosemirror/view';
import { executeAiManagedObject as executeAiManagedObjectRequest } from '@atlassian/ai-managed-objects/api';
import type { AiManagedObject } from '@atlassian/ai-managed-objects/types';

import { pluginKey } from '../../common/agentManagedExtensionPluginType';

import {
	dispatchAgentManagedExtensionMeta,
	replaceNodeBodyContent,
} from './agentManagedObjectNodeMetadata';

type UseAiManagedExecutionArgs = {
	editorView: EditorView;
};

type ExecuteAiManagedObjectArgs = {
	aimoId: string;
	localId?: string;
};

type UseAiManagedExecutionResult = {
	executeAiManagedObject: (args: ExecuteAiManagedObjectArgs) => Promise<AiManagedObject | null>;
};

let executionRequestSequence = 0;

const createExecutionRequestId = (): string => `execution-${++executionRequestSequence}`;

export const useAiManagedExecution = ({
	editorView,
}: UseAiManagedExecutionArgs): UseAiManagedExecutionResult => {
	const setExecutionLoading = useCallback(
		({
			isExecuting,
			localId,
			requestId,
		}: {
			isExecuting: boolean;
			localId?: string;
			requestId: string;
		}): void => {
			if (!localId) {
				return;
			}

			dispatchAgentManagedExtensionMeta(editorView, {
				localId,
				requestId,
				type: isExecuting ? 'START_EXECUTION' : 'FINISH_EXECUTION',
			});
		},
		[editorView],
	);

	const isLatestExecutionRequest = useCallback(
		({ localId, requestId }: { localId?: string; requestId: string }): boolean => {
			if (!localId) {
				return true;
			}

			const pluginState = pluginKey.getState(editorView.state);
			if (!pluginState) {
				return true;
			}

			return pluginState.executingLocalIds[localId] === requestId;
		},
		[editorView],
	);

	const executeAiManagedObject = useCallback(
		async ({ aimoId, localId }: ExecuteAiManagedObjectArgs): Promise<AiManagedObject | null> => {
			const requestId = createExecutionRequestId();
			setExecutionLoading({ isExecuting: true, localId, requestId });
			try {
				const executionResult = await executeAiManagedObjectRequest(aimoId);

				if (!isLatestExecutionRequest({ localId, requestId })) {
					return null;
				}

				const executeTr = editorView.state.tr;
				const didReplaceBody = replaceNodeBodyContent(
					editorView.state,
					executeTr,
					executionResult.output.blockContent,
					localId,
				);

				if (didReplaceBody) {
					editorView.dispatch(executeTr);
				}

				return executionResult.aiManagedObject;
			} catch {
				// Save already succeeded and the popup has closed. Runtime error presentation
				// is owned by the follow-up managed block runtime lifecycle work.
				return null;
			} finally {
				setExecutionLoading({ isExecuting: false, localId, requestId });
			}
		},
		[editorView, isLatestExecutionRequest, setExecutionLoading],
	);

	return { executeAiManagedObject };
};
